import type { ChartAnalyticsDefinition } from '@/features/chart/lib/config/chart-analytics'
import { getEffectiveStudyInputs } from '@/features/chart/lib/config/chart-analytics'
import {
  type ChartSeriesSourceId,
  REFRESH_NONCE_INPUT_ID,
  type SeriesFetchContext,
  type SeriesInputValues,
} from '@/lib/tradingview/datafeeds/series-sources'
import type { Exchange } from '@/types/generated/common/types'

export type SeriesSubscriptionEntry = { sourceId: ChartSeriesSourceId; ctx: SeriesFetchContext }

export type SeriesCtxBase = {
  symbol: string
  exchange: Exchange
  timeframeSeconds: number
  fromSec: number
  toSec: number
}

// Same lookback the candle warmup uses (use-tv-widget.ts warmUpPastHistory).
export const SERIES_BACKFILL_LOOKBACK_BARS = 120
export const SERIES_BACKFILL_MIN_LOOKBACK_SEC = 24 * 60 * 60

/** Backfill window for own-fetch series: the visible range padded left by the
 *  warmup lookback, extending to now. */
export function computeSeriesBackfillWindow(
  visibleRange: { from: number; to: number } | null,
  timeframeSeconds: number,
  nowSec: number
): { fromSec: number; toSec: number } {
  const lookbackSec = Math.max(
    Math.max(1, timeframeSeconds) * SERIES_BACKFILL_LOOKBACK_BARS,
    SERIES_BACKFILL_MIN_LOOKBACK_SEC
  )
  const from = visibleRange && Number.isFinite(visibleRange.from) ? visibleRange.from : nowSec
  return { fromSec: Math.floor(from - lookbackSec), toSec: nowSec }
}

/**
 * Desired series subscriptions for the current chart state: one entry per
 * own-fetch study instance (array-based study map — duplicates refcount).
 * Inputs are the definition defaults overlaid with the study's live input
 * values; the hidden refreshNonce never reaches the cache key.
 */
export function buildSeriesSubscriptionEntries(
  studyMap: ReadonlyMap<string, string[]>,
  definitionsByKey: Readonly<Record<string, ChartAnalyticsDefinition>>,
  ctxBase: SeriesCtxBase,
  readInputsForStudy: (studyId: string) => SeriesInputValues | null
): SeriesSubscriptionEntry[] {
  const entries: SeriesSubscriptionEntry[] = []
  for (const [metric, studyIds] of studyMap) {
    const definition = definitionsByKey[metric]
    if (!definition?.source) continue
    const defaults: SeriesInputValues = Object.fromEntries(
      getEffectiveStudyInputs(definition).map((spec) => [spec.id, spec.defval])
    )
    for (const studyId of studyIds) {
      const live = readInputsForStudy(studyId)
      // The hidden refreshNonce must never reach the cache key — strip it once,
      // after the defaults/live merge, covering whichever side contributed it.
      const inputs: SeriesInputValues = { ...defaults, ...(live ?? {}) }
      delete inputs[REFRESH_NONCE_INPUT_ID]
      entries.push({ sourceId: definition.source, ctx: { ...ctxBase, inputs } })
    }
  }
  return entries
}
